import { FC, useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ModalDialog, ModalDialogBody, ModalDialogTitle } from '../../../common/components/ModalDialog'
import { LoadingSpinner } from '../../../common/components/LoadingSpinner'
import { apiClient } from '../../../api/ApiClient'
import { routePaths } from '../../../common/routing/routePaths'
import { useShippingAddresses } from '../model/useShippingAddresses'
import { CreateQuoteFormData } from '../model/types'
import { CreateQuoteFormStep } from './CreateQuoteFormStep'
import { CreateQuoteConfirmationStep } from './CreateQuoteConfirmationStep'

const WideDialogBody = styled(ModalDialogBody)({
  width: 480,
  maxWidth: '100%',
  textAlign: 'start',
})

const LoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 8,
  padding: theme.spacing.xl,
}))

interface CreateQuoteDialogProps {
  opened: boolean
  onClose: () => void
  chatId: string
}

export const CreateQuoteDialog: FC<CreateQuoteDialogProps> = ({ opened, onClose, chatId }) => {
  return (
    <ModalDialog opened={opened} onClose={onClose}>
      <CreateQuoteDialogContent chatId={chatId} />
    </ModalDialog>
  )
}

const CreateQuoteDialogContent: FC<{ chatId: string }> = ({ chatId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { addresses, defaultAddress, loading: addressesLoading } = useShippingAddresses(chatId)

  const [step, setStep] = useState<1 | 2>(1)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState<CreateQuoteFormData>({
    referenceNumber: '',
    shippingAddressId: '',
    customShippingCost: 0,
  })

  useEffect(() => {
    if (defaultAddress && !formData.shippingAddressId) {
      setFormData((prev) => ({
        ...prev,
        shippingAddressId: defaultAddress.id,
        customShippingCost: defaultAddress.price.value,
      }))
    }
  }, [defaultAddress, formData.shippingAddressId])

  const handleConfirm = useCallback(async () => {
    setSubmitting(true)
    try {
      const quote = await apiClient.quotes.createQuote({
        chatId,
        referenceNumber: formData.referenceNumber,
        shippingAddressId: formData.shippingAddressId,
        customShippingCost: formData.customShippingCost,
      })
      navigate(routePaths.quote(quote.id), { state: quote })
    } finally {
      setSubmitting(false)
    }
  }, [chatId, formData, navigate])

  return (
    <WideDialogBody>
      <ModalDialogTitle>{t('Create quote')}</ModalDialogTitle>

      {addressesLoading ? (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      ) : step === 1 ? (
        <CreateQuoteFormStep
          addresses={addresses}
          formData={formData}
          onFormDataChange={setFormData}
          onNext={() => setStep(2)}
        />
      ) : (
        <CreateQuoteConfirmationStep
          formData={formData}
          addresses={addresses}
          loading={submitting}
          onBack={() => setStep(1)}
          onConfirm={handleConfirm}
        />
      )}
    </WideDialogBody>
  )
}
